//React
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
//Libraries
import cx from "classnames";
//Components
import {
  Card,
  CodeExampleModalModal,
  DocsCodeBlock,
  DocsFlexSectionBlock,
  DocsScreen,
  FlexBox,
  TitleBlock,
  Image,
} from "components";
//Constants
import { docsConstants } from "../../../constants";
//Utils
import { useIsInViewport } from "utils";
//Styles
import "./styles.scss";
//Assets
import FlutterIcon from "assets/icons/flutter.png";
import FlutterSdkImg1 from "assets/docs/flutter-1.png";
import FlutterSdkImg2 from "assets/docs/flutter-2.png";
import FlutterSdkImg3 from "assets/docs/flutter-3.png";
import FlutterSdkImg4 from "assets/docs/flutter-4.png";
import ROUTE_PATH from "Routes/paths";

const DocsHome = () => {
  /* Constants */
  //States
  const [scrolledToElement, setScrolledToElement]: any = useState({
    selectedTab: docsConstants.TITLES.OVERVIEW,
  });
  const [codeExamplePath, setCodeExamplePath]: any = useState("");

  //Params
  const sdkType: any = useParams();

  //Refs
  const WelcomeSectionRef: any = useRef();
  const isWelcomeSectionInViewport = useIsInViewport(WelcomeSectionRef);
  //Flutter Sdk
  const InstallRef: any = useRef();
  const isInstallInViewport = useIsInViewport(InstallRef);
  const ImportRef: any = useRef();
  const isImportInViewport = useIsInViewport(ImportRef);
  const InstanceRef: any = useRef();
  const isInstanceInViewport = useIsInViewport(InstanceRef);
  const MethodsRef: any = useRef();
  const isMethodsInViewport = useIsInViewport(MethodsRef);
  const NativePartRef: any = useRef();
  const isNativePartInViewport = useIsInViewport(NativePartRef);

  //Jsons
  const scrollAccToSpecificElementMap = {
    [docsConstants.TITLES.OVERVIEW]: WelcomeSectionRef,
    [docsConstants.TITLES.FLUTTER.NATIVE_PART]: NativePartRef,
    [docsConstants.TITLES.FLUTTER.INSTALL]: InstallRef,
  };

  /* useEffects */
  useEffect(() => {
    if (isWelcomeSectionInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.OVERVIEW,
      });
    } else if (isInstallInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.FLUTTER.INSTALL,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isImportInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.FLUTTER.IMPORT,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isInstanceInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.FLUTTER.INSTANCE,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isMethodsInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.FLUTTER.METHOD,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isNativePartInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.FLUTTER.NATIVE_PART,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    }
  }, [
    isWelcomeSectionInViewport,
    //flutter sdk
    isInstallInViewport,
    isImportInViewport,
    isInstanceInViewport,
    isMethodsInViewport,
    isNativePartInViewport,
  ]);

  /* Functions */
  //Scroll To Specific element
  const scrollToSpecificElement = (selectedTab) => {
    scrollAccToSpecificElementMap[selectedTab]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  //Render UI
  const WelcomeSection = (
    <div ref={WelcomeSectionRef}>
      <TitleBlock
        title={
          <FlexBox alignCenter className="section-header">
            <Image src={FlutterIcon} className="mr-r-10" />
            <p>Flutter SDK</p>
          </FlexBox>
        }
        content={
          <p className="fs-16">
            The Flutter SDK help you get up and running with OTPless faster by
            avoiding the struggles associated with to handle frontend functions.
          </p>
        }
        titleClass="fs-32 font-heavy"
        className="animated fadeInUp mr-b-60"
      />
    </div>
  );

  const FlutterSdk = () => {
    const iOSRequiredPermissions = (
      <div className="api">
        <DocsFlexSectionBlock
          leftSection={
            <div className="fs-14">
              <ul className="dots">
                <li>
                  add the following keys in Info.plist file for sucessfull
                  deeplinking of callback uri
                </li>
                <li>
                  call back URI has two parts one is scheme and another is
                  domain
                </li>
                <li>
                  for Example: For URI "exam://example.com", "exam" will be
                  scheme and "example.com" will be the domain
                </li>
                <li>
                  we need to add the domain and scheme to the Info.Plist file
                </li>
                <li
                  onClick={() => {
                    setCodeExamplePath(FlutterSdkImg3);
                  }}
                  className="clicking-animation underline"
                >
                  Check Example
                </li>
              </ul>
            </div>
          }
          rightSection={
            <>
              <DocsCodeBlock
                title={"iOS"}
                codeToBeCopied={`
  <key>FlutterDeepLinkingEnabled</key>
  <true/>
  <key>CFBundleURLTypes</key>
  <array>
      <dict>
      <key>CFBundleTypeRole</key>
      <string>Editor</string>
      <key>CFBundleURLName</key>
      <string>your_app_domain</string>
      <key>CFBundleURLSchemes</key>
      <array>
      <string>your_app_scheme</string>
      </array>
      </dict>
  </array>
              `}
                code={
                  <>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">key</span>
                      <span className="comment">&#62;</span>
                      <span className="mr-h-5">FlutterDeepLinkingEnabled</span>
                      <span className="comment">&#60;/</span>
                      <span className="tag">key</span>
                      <span className="comment">&#62;</span>
                    </p>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">true</span>
                      <span className="comment"> /&#62;</span>
                    </p>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">key</span>
                      <span className="comment">&#62;</span>
                      <span className="mr-h-5">CFBundleURLTypes</span>
                      <span className="comment">&#60;/</span>
                      <span className="tag">key</span>
                      <span className="comment">&#62;</span>
                    </p>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">array</span>
                      <span className="comment">&#62;</span>
                    </p>
                    <div className="mr-l-20">
                      <p>
                        <span className="comment">&#60;</span>
                        <span className="tag">dict</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p>
                        <span className="comment">&#60;</span>
                        <span className="tag">key</span>
                        <span className="comment">&#62;</span>
                        <span className="mr-h-5">CFBundleTypeRole</span>
                        <span className="comment">&#60;/</span>
                        <span className="tag">key</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p>
                        <span className="comment">&#60;</span>
                        <span className="tag">string</span>
                        <span className="comment">&#62;</span>
                        <span className="mr-h-5">Editor</span>
                        <span className="comment">&#60;/</span>
                        <span className="tag">string</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p>
                        <span className="comment">&#60;</span>
                        <span className="tag">key</span>
                        <span className="comment">&#62;</span>
                        <span className="mr-h-5">CFBundleURLName</span>
                        <span className="comment">&#60;/</span>
                        <span className="tag">key</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p>
                        <span className="comment">&#60;</span>
                        <span className="tag">string</span>
                        <span className="comment">&#62;</span>
                        <span className="mr-h-5">your_app_domain</span>
                        <span className="comment">&#60;/</span>
                        <span className="tag">string</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p>
                        <span className="comment">&#60;</span>
                        <span className="tag">key</span>
                        <span className="comment">&#62;</span>
                        <span className="mr-h-5">CFBundleURLSchemes</span>
                        <span className="comment">&#60;/</span>
                        <span className="tag">key</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p>
                        <span className="comment">&#60;</span>
                        <span className="tag">array</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p className="mr-l-20">
                        <span className="comment">&#60;</span>
                        <span className="tag">string</span>
                        <span className="comment">&#62;</span>
                        <span className="mr-h-5">your_app_scheme</span>
                        <span className="comment">&#60;/</span>
                        <span className="tag">string</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p>
                        <span className="comment">&#60;/</span>
                        <span className="tag">array</span>
                        <span className="comment">&#62;</span>
                      </p>
                      <p>
                        <span className="comment">&#60;/</span>
                        <span className="tag">dict</span>
                        <span className="comment">&#62;</span>
                      </p>
                    </div>
                    <p>
                      <span className="comment">&#60;/</span>
                      <span className="tag">array</span>
                      <span className="comment">&#62;</span>
                    </p>
                  </>
                }
              />
              {/* <DocsCodeBlock
                title={"Example"}
                code={<Image src={FlutterSdkImg3} className="w-100" />}
                className="img-flex mr-t-40"
              /> */}
            </>
          }
        />
        <DocsFlexSectionBlock
          leftSection={
            <div className="fs-14">
              <ul className="dots">
                <li>
                  We also need the query permission to check whether the user
                  has Whatsapp installed in his/her device
                </li>
                <li
                  onClick={() => {
                    setCodeExamplePath(FlutterSdkImg4);
                  }}
                  className="clicking-animation underline"
                >
                  Check Example
                </li>
              </ul>
            </div>
          }
          rightSection={
            <>
              <DocsCodeBlock
                title={"Whatsapp"}
                codeToBeCopied={`
  <key>LSApplicationQueriesSchemes</key>
  <array>
      <string>whatsapp</string>
  </array>
              `}
                code={
                  <>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">key</span>
                      <span className="comment">&#62;</span>
                      <span className="mr-h-5">
                        LSApplicationQueriesSchemes
                      </span>
                      <span className="comment">&#60;/</span>
                      <span className="tag">key</span>
                      <span className="comment">&#62;</span>
                    </p>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">array</span>
                      <span className="comment">&#62;</span>
                    </p>
                    <div className="mr-l-40">
                      <p>
                        <span className="comment">&#60;</span>
                        <span className="tag">string</span>
                        <span className="comment">&#62;</span>
                        <span className="mr-h-5">whatsapp</span>
                        <span className="comment">&#60;/</span>
                        <span className="tag">string</span>
                        <span className="comment">&#62;</span>
                      </p>
                    </div>
                    <p>
                      <span className="comment">&#60;/</span>
                      <span className="tag">array</span>
                      <span className="comment">&#62;</span>
                    </p>
                  </>
                }
                className="mr-t-40"
              />
              {/* <DocsCodeBlock
                title={"Example"}
                code={<Image src={FlutterSdkImg4} className="w-100" />}
                className="img-flex mr-t-40"
              /> */}
            </>
          }
        />
      </div>
    );

    const AndroidRequiredPermissions = (
      <div className="api mr-b-40">
        <DocsFlexSectionBlock
          leftSection={
            <ul className="dots fs-14">
              <li>
                The AndroidMainfest.xml must be modified to for deeplinking
              </li>
              <li>
                <p>
                  Paste this xml snippet in AndroidManifest.xml present inside{" "}
                  <span className="highlighted font-light pd-h-5">
                    android/app/src/main/AndroidManifest.xml
                  </span>{" "}
                  inside the{" "}
                  <span className="highlighted font-light pd-h-5">
                    &#60;activity&#62;
                  </span>{" "}
                  tag
                </p>
              </li>
              <li>
                If you got more doubt please check the example app's
                AndroidManifest
              </li>
            </ul>
          }
          rightSection={
            <DocsCodeBlock
              title={"Android"}
              codeToBeCopied={`
  <intent-filter android:autoVerify="true">
      <action android:name="android.intent.action.VIEW" />
      <category android:name="android.intent.category.DEFAULT" />
      <category android:name="android.intent.category.BROWSABLE" />
      <data android:scheme="your_app_scheme" android:host="your_app_domain"  />
  </intent-filter>
                `}
              code={
                <>
                  <p>
                    <span className="comment">&#60;</span>
                    <span className="tag">intent-filter</span>
                    <span className="attr-name mr-l-5">android</span>
                    <span className="tag mr-h-5">:</span>
                    <span className="attr-name">autoVerify</span>
                    <span className="tag mr-h-5">=</span>
                    <span className="tag">"</span>
                    <span className="attr-value">true</span>
                    <span className="tag">"</span>
                    <span className="comment">&#62;</span>
                  </p>
                  <div className="mr-l-20">
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">action</span>
                      <span className="attr-name mr-l-5">android</span>
                      <span className="tag mr-h-5">:</span>
                      <span className="attr-name">name</span>
                      <span className="tag mr-h-5">=</span>
                      <span className="tag">"</span>
                      <span className="attr-value">
                        android.intent.action.VIEW
                      </span>
                      <span className="tag">"</span>
                      <span className="comment"> /&#62;</span>
                    </p>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">category</span>
                      <span className="attr-name mr-l-5">android</span>
                      <span className="tag mr-h-5">:</span>
                      <span className="attr-name">name</span>
                      <span className="tag mr-h-5">=</span>
                      <span className="tag">"</span>
                      <span className="attr-value">
                        android.intent.category.DEFAULT
                      </span>
                      <span className="tag">"</span>
                      <span className="comment"> /&#62;</span>
                    </p>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">category</span>
                      <span className="attr-name mr-l-5">android</span>
                      <span className="tag mr-h-5">:</span>
                      <span className="attr-name">name</span>
                      <span className="tag mr-h-5">=</span>
                      <span className="tag">"</span>
                      <span className="attr-value">
                        android.intent.category.BROWSABLE
                      </span>
                      <span className="tag">"</span>
                      <span className="comment"> /&#62;</span>
                    </p>
                    <p>
                      <span className="comment">&#60;</span>
                      <span className="tag">data</span>
                      <span className="attr-name mr-l-5">android</span>
                      <span className="tag mr-h-5">:</span>
                      <span className="attr-name">scheme</span>
                      <span className="tag mr-h-5">=</span>
                      <span className="tag">"</span>
                      <span className="attr-value">your_app_scheme</span>
                      <span className="tag">"</span>
                      <span className="attr-name mr-l-10">android</span>
                      <span className="tag mr-h-5">:</span>
                      <span className="attr-name">host</span>
                      <span className="tag mr-h-5">=</span>
                      <span className="tag">"</span>
                      <span className="attr-value">your_app_domain</span>
                      <span className="tag">"</span>
                      <span className="comment"> /&#62;</span>
                    </p>
                  </div>
                  <p>
                    <span className="comment">&#60;/</span>
                    <span className="tag">intent-filter</span>
                    <span className="comment">&#62;</span>
                  </p>
                </>
              }
            />
          }
        />
      </div>
    );

    const NativePart = (
      <div ref={NativePartRef}>
        <TitleBlock
          title={`${docsConstants.TITLES.FLUTTER.NATIVE_PART} (Permissions)`}
          content={
            <ul className="decimal fs-16">
              <li>
                <p className="fs-16 font-medium mr-t-20">For iOS</p>
                {iOSRequiredPermissions}
              </li>
              <li>
                <p className="fs-16 font-medium pd-t-20 dashed-border-top mr-t-40">
                  For Android
                </p>
                {AndroidRequiredPermissions}
              </li>
            </ul>
          }
          titleClass="fs-24 font-heavy"
          className="pd-t-40 border-top mr-t-40"
        />
      </div>
    );

    const FlutterPart = (
      <>
        <div ref={InstallRef}>
          <TitleBlock
            title={`1. ${docsConstants.TITLES.FLUTTER.INSTALL}`}
            content={
              <div className="api">
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-14">
                      <p>
                        Enter this command in terminal at the root of your
                        flutter app
                      </p>
                      {/* <p className="mr-t-20">Install OTPless Flutter plugin</p>
                      <ul className="dots">
                        <li>
                          Enter this command in terminal at the root of your
                          flutter app
                        </li>
                      </ul> */}
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"Install"}
                      codeToBeCopied={`
  flutter pub add otpless_flutter
                    `}
                      code={
                        <>
                          <p>
                            <span className="tag">flutter</span>
                            <span className="attr-name mr-h-5">pub add</span>
                            <span className="attr-value">otpless_flutter</span>
                          </p>
                        </>
                      }
                    />
                  }
                />
              </div>
            }
            titleClass="fs-24 font-heavy"
          />
        </div>
        <div ref={ImportRef}>
          <TitleBlock
            title={`2. ${docsConstants.TITLES.FLUTTER.IMPORT}`}
            content={
              <div className="api">
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-14">
                      <p> Use the plugin in application's entry point</p>
                      <p className="fs-12">
                        Eg. in the root route (starting page) "/"
                      </p>
                      {/* <p className="">
                        Import the plugin in your Flutter project
                      </p>
                      <p className="fs-12 mr-t-10 font-medium">
                        NOTE : USE THE PLUGIN IN APPLICATION'S ENTRY POINT Eg.
                        in the root route (starting page) "/"
                      </p> */}
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"Import"}
                      codeToBeCopied={`
  import 'package:otpless_flutter/otpless_flutter.dart';
                    `}
                      code={
                        <>
                          <p className="attr-value">
                            <span className="attr-name">import</span>{" "}
                            "package:otpless_flutter/otpless_flutter.dart"
                          </p>
                        </>
                      }
                    />
                  }
                  className="mr-t-10"
                />
              </div>
            }
            titleClass="fs-24 font-heavy"
            className="mr-t-40 pd-t-40 dashed-border-top"
          />
        </div>
        <div ref={InstanceRef}>
          <TitleBlock
            title={`3. ${docsConstants.TITLES.FLUTTER.INSTANCE}`}
            content={
              <div className="api">
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-14">
                      <p className="">
                        Create an instance of plugin and store in a local
                        variable
                      </p>
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"Instance"}
                      codeToBeCopied={`
  final _otplessFlutterPlugin = Otpless();
                    `}
                      code={
                        <>
                          <p>
                            <span className="attr-name">final</span>{" "}
                            <span className="attr-value">
                              _otplessFlutterPlugin
                            </span>{" "}
                            <span className="tag mr-h-5">=</span>
                            <span className="attr-value">Otpless</span>
                            <span className="tag">()</span>;
                          </p>
                        </>
                      }
                    />
                  }
                  className="mr-t-10"
                />
              </div>
            }
            titleClass="fs-24 font-heavy"
            className="mr-t-40 pd-t-40 dashed-border-top"
          />
        </div>
        <div ref={MethodsRef}>
          <TitleBlock
            title={`4. ${docsConstants.TITLES.FLUTTER.METHOD}`}
            content={
              <div className="api">
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-14">
                      <p className="">
                        Call this function onclick of your{" "}
                        <span className="font-medium">Login with WhatsApp</span>{" "}
                        button to redirect the user to WhatsApp. To get the{" "}
                        <span className="highlighted font-light pd-h-5">
                          intentUri
                        </span>{" "}
                        , checkout the{" "}
                        <a
                          className="underline clicking-animation"
                          href={ROUTE_PATH.DOCS.API_REFERENCE}
                          target="_self"
                        >
                          Rest API
                        </a>{" "}
                        section
                      </p>
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"onClick"}
                      codeToBeCopied={`
  // ** Function to initiate the login process
  void initiateWhatsappLogin(String intentUrl) async{
    var result = await _otplessFlutterPlugin.loginUsingWhatsapp(intentUrl: intentUrl);
    switch (result['code']) {
      case "581":
        print(result['message']);
        //TODO: handle whatsapp not found
        break;
      default:
    }
  }
                    `}
                      code={
                        <>
                          <p className="comment">
                            // ** Function to initiate the login process
                          </p>
                          <p>
                            <span className="attr-name">void</span>
                            <span className="attr-value mr-h-5">
                              initiateWhatsappLogin
                            </span>
                            <span className="tag">(</span>
                            <span className="attr-name mr-h-5">String</span>
                            <span className="attr-value mr-r-5">intentUrl</span>
                            <span className="tag">)</span>
                            <span className="punctuation mr-h-5">async</span>
                            <span className="tag mr-h-5">&#123;</span>
                          </p>
                          <div className="mr-l-20">
                            <p>
                              <span className="punctuation">var</span>
                              <span className="attr-name mr-l-5">result</span>
                              <span className="tag mr-h-5">=</span>
                              <span className="punctuation">await </span>
                              <span className="attr-value">
                                _otplessFlutterPlugin
                              </span>
                              .
                              <span className="attr-value">
                                loginUsingWhatsapp
                              </span>
                              <span className="tag mr-h-5">(</span>
                              <span className="attr-value">intentUrl</span>
                              <span className="tag mr-h-5">:</span>
                              <span className="attr-value">intentUrl</span>
                              <span className="tag mr-h-5">)</span>
                            </p>
                            <p className="mr-t-10">
                              <span className="punctuation">switch</span>
                              <span className="tag mr-h-5">(</span>
                              <span className="attr-name">result</span>
                              <span className="tag mr-h-5">[</span>
                              <span className="attr-value">"code"</span>
                              <span className="tag mr-h-5">]</span>
                              <span className="tag mr-h-5">)</span>
                              <span className="tag">&#123;</span>
                            </p>
                            <div className="mr-l-20">
                              <p>
                                <span className="attr-name">case</span>
                                <span className="attr-value mr-h-5">"581"</span>
                                <span className="tag">:</span>
                              </p>
                              <div className="mr-l-20">
                                <p>
                                  <span className="punctuation">print</span>
                                  <span className="tag mr-h-5">(</span>
                                  <span className="attr-name">result</span>
                                  <span className="tag mr-h-5">[</span>
                                  <span className="attr-value">"message"</span>
                                  <span className="tag mr-h-5">]</span>
                                  <span className="tag mr-h-5">)</span>
                                  <span className="tag">&#123;</span>
                                </p>
                                <p className="comment">
                                  //TODO: handle whatsapp not found
                                </p>
                                <p>
                                  <span className="attr-name">break</span>;
                                </p>
                              </div>
                              <p className="mr-t-10 attr-name">default:</p>
                            </div>
                            <p className="tag mr-t-5">&#125;</p>
                          </div>
                          <p className="tag">&#125;</p>
                        </>
                      }
                    />
                  }
                  className="mr-t-10"
                />
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-14">
                      <p>
                        Call this function to fetch the token from redirection
                        URL
                      </p>
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"Fetch Token"}
                      codeToBeCopied={`
  @override
  void initState() {
    super.initState();
    initPlatformState();
  }

  // ** Function that is called when page is loaded
  // ** We can check the auth state in this function
  Future<void> initPlatformState() async {
    _otplessFlutterPlugin.authStream.listen((token) {
      // TODO: Handle user token like storing in SharedPreferences or navigation
      print(token)
    });
  }
                    `}
                      code={
                        <>
                          <p className="punctuation">@Override</p>
                          <p>
                            <span className="attr-name">void</span>
                            <span className="attr-value mr-h-5">initState</span>
                            <span className="tag">()</span>
                            <span className="tag mr-h-5">&#123;</span>
                          </p>
                          <p className="attr-value mr-l-20">
                            <span className="attr-name">super</span>
                            .initState<span className="tag">()</span>;
                          </p>
                          <p className="attr-value mr-l-20">
                            initPlatformState<span className="tag">()</span>;
                          </p>
                          <p className="tag">&#125;</p>
                          {/* Second Function */}
                          <p className="comment mr-t-40">
                            // ** Function that is called when page is loaded
                          </p>
                          <p className="comment">
                            // ** We can check the auth state in this function
                          </p>
                          <p>
                            <span className="punctuation">private</span>
                            <span className="tag mr-l-5">&#60;</span>
                            <span className="attr-name">void</span>
                            <span className="tag mr-r-5">&#62;</span>
                            <span className="attr-value mr-r-5">
                              initPlatformState
                            </span>
                            <span className="tag">()</span>
                            <span className="punctuation mr-l-5">async</span>
                            <span className="tag mr-h-5">&#123;</span>
                          </p>
                          <div className="mr-l-20">
                            <p>
                              <span className="attr-value">
                                _otplessFlutterPlugin
                              </span>
                              .<span className="attr-value">authStream</span>.
                              <span className="attr-value">listen</span>
                              <span className="tag mr-l-5">(</span>
                              <span className="tag mr-r-5">(</span>
                              <span className="attr-value">token</span>
                              <span className="tag mr-h-5">)</span>
                              <span className="tag">&#123;</span>
                            </p>
                            <div className="mr-l-20">
                              <p className="comment">
                                // TODO: Handle user token like storing in
                                SharedPreferences or navigation
                              </p>
                              <p>
                                <span className="attr-name">print</span>
                                <span className="tag">(</span>
                                <span className="attr-value mr-h-5">token</span>
                                <span className="tag">)</span>
                              </p>
                            </div>
                            <p className="tag">&#125;)</p>
                          </div>
                          <p className="tag">&#125;</p>
                        </>
                      }
                    />
                  }
                  className="mr-t-40"
                />
              </div>
            }
            titleClass="fs-24 font-heavy"
            className="mr-t-40 pd-t-40 dashed-border-top"
          />
        </div>
      </>
    );

    return (
      <TitleBlock
        title={""}
        content={
          <>
            {FlutterPart}
            {NativePart}
          </>
        }
        titleClass="fs-24 font-heavy"
        className="animated fadeInUp delay-01s border-top pd-t-30 mr-b-60"
      />
    );
  };

  //Return UI
  return (
    <DocsScreen
      className="docs-home-screen"
      hasSideMenu
      hasHeader
      screenAnimationClass="no-animation"
      tabSelectCallback={scrollToSpecificElement}
      scrolledToElement={{ ...scrolledToElement }}
    >
      {WelcomeSection}
      {FlutterSdk()}
      <CodeExampleModalModal
        visible={codeExamplePath}
        onClose={() => {
          setCodeExamplePath("");
        }}
        path={codeExamplePath}
      />
    </DocsScreen>
  );
};

export default DocsHome;
